import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/navigation.css';
import { useAuth } from '../context/AuthContext';
import { useFeature } from '../context/FeatureFlagsContext';
import { FF_LESSON_MODE } from '../config/featureFlags';
import { useTheme } from '../context/ThemeContext';
import { useDashboardLayout } from '../context/DashboardLayoutContext';
import { BASE_URL } from '../api/client';
import { vocabularyService } from '../api/vocabularyService';
import { useStatsRefresh } from '../context/StatsRefreshContext';
import { VocabularyItem } from './vocabulary/VocabularyItem';
import StreakBadge from './streak/StreakBadge';
import MicButton from './common/MicButton';
import logo from '../assets/logo.png';
import { getTierFromDifficulty } from '../hooks/useProficiencyTier';
function Navigation() {
    const { t } = useTranslation('navigation');
    const auth = useAuth();
    const lessonModeEnabled = useFeature(FF_LESSON_MODE);
    const tier = getTierFromDifficulty(auth?.userSettings?.learning_settings?.difficulty_level);
    const { overview, streak: sharedStreak } = useStatsRefresh();
    const { theme, toggleTheme } = useTheme();
    const { layoutMode, viewMode, setLayoutMode, setViewMode } = useDashboardLayout();
    const handleToggleLayout = () => setLayoutMode(layoutMode === 'cards' ? 'table' : 'cards');
    const handleToggleView = () => {
        if (viewMode === 'last10') setViewMode('paginated');
        else if (viewMode === 'paginated') setViewMode('fullList');
        else setViewMode('last10');
    };
    const [avatarKey, setAvatarKey] = useState(Date.now());
    const [showAvatar, setShowAvatar] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const [searchBuffer, setSearchBuffer] = useState(searchParams.get('search') || '');
    const [searchResults, setSearchResults] = useState<VocabularyItem[]>([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const [totalItems, setTotalItems] = useState<number | null>(null);
    const [streakDays, setStreakDays] = useState<number | null>(null);
    const [todayXP, setTodayXP] = useState<number | undefined>(undefined);
    const [showReviewBadge, setShowReviewBadge] = useState(false);
    const location = useLocation();
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const topNavRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [menuHeight, setMenuHeight] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (isMenuOpen && topNavRef.current && headerRef.current) {
            const navBottom = topNavRef.current.getBoundingClientRect().bottom;
            const headerBottom = headerRef.current.getBoundingClientRect().bottom;
            const navHeight = navBottom - headerBottom;
            requestAnimationFrame(() => {
                const contentHeight = (menuRef.current?.scrollHeight ?? 0) + 76;
                setMenuHeight(Math.max(navHeight, contentHeight));
            });
        }
    }, [isMenuOpen]);

    useEffect(() => {
        if (!isMenuOpen) return;
        const handleMouseMove = (e: MouseEvent) => {
            if (!menuRef.current) return;
            const rect = menuRef.current.getBoundingClientRect();
            if (e.clientX < rect.left || e.clientX > rect.right ||
                e.clientY < rect.top || e.clientY > rect.bottom) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, [isMenuOpen]);
    const isWordPage = () => ['/addWord', '/editWord'].includes(location.pathname);
    // Email signups may have no name (onboarding no longer asks for it) — fall
    // back to the username's first letter, like mobile's AppTopNav.
    const initials = [auth?.user?.first_name, auth?.user?.last_name]
        .filter(Boolean).map(s => s!.charAt(0).toUpperCase()).join('')
        || auth?.user?.username?.charAt(0).toUpperCase() || '?';
    const navigate = useNavigate();

    useEffect(() => {
        setAvatarKey(Date.now());
        setShowAvatar(true);
    }, [auth?.user.image_profile_id]);

    useEffect(() => {
        const onAvatarUpdated = () => {
            setAvatarKey(Date.now());
            setShowAvatar(true);
            auth?.refreshUserData?.();
        };
        window.addEventListener('avatar-updated', onAvatarUpdated);
        return () => window.removeEventListener('avatar-updated', onAvatarUpdated);
    }, [auth]);

    // Overview (total_words/streak_days) now comes from the shared StatsRefreshContext fetch
    // instead of an independent statsService.getOverview call here — Navigation, the sidebar
    // Progress/Challenge widgets, and RecentActivity were each fetching the exact same
    // /api/v1/stats/overview response on every page load.
    useEffect(() => {
        setTotalItems(overview?.total_words ?? null);
        setStreakDays(overview?.streak_days ?? null);
    }, [overview]);

    // Only todayXP comes from the streak (overview doesn't expose it); streakDays stays on
    // `overview` above so both update together. The streak itself is fetched once in
    // StatsRefreshContext and shared with the Challenge widget, not fetched again here.
    useEffect(() => {
        if (sharedStreak) setTodayXP(sharedStreak.today_xp);
    }, [sharedStreak]);

    // Mirrors weavewords-mobile's MainTabs.tsx ReviewTab badge dot exactly:
    // lit only while today's due queue hasn't been touched yet (reviews_today
    // === 0), not merely while due_today > 0 — so it disappears the moment the
    // user reviews a single word today, rather than staying lit until the
    // whole queue is cleared. Derived from the same shared `overview` as
    // total_words/streak_days above (not a separate spacedRepetitionService
    // fetch) — overview.review carries the identical due_today/reviews_today
    // fields, and StatsRefreshContext already re-fetches overview on
    // statsVersion changes, so this clears right after a review session
    // (refreshStats()) without a second, redundant network request.
    useEffect(() => {
        setShowReviewBadge((overview?.review?.due_today ?? 0) > 0 && (overview?.review?.reviews_today ?? 0) === 0);
    }, [overview]);

    const handleVoiceResult = (text: string) => {
        setSearchBuffer(text);
        if (isWordPage()) {
            vocabularyService.getVocabulary({ page: 1, pageSize: 1000 }, { search: text, tags: [] })
                .then(response => {
                    const items = response?.items ?? [];
                    setSearchResults(items);
                    setShowSearchDropdown(items.length > 0);
                });
        } else {
            navigate(`/dashboard?search=${encodeURIComponent(text)}`);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchBuffer(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (isWordPage()) {
            if (value.length >= 3 || value.length === 0) {
                debounceRef.current = setTimeout(async () => {
                    if (value.length === 0) {
                        setSearchResults([]);
                        setShowSearchDropdown(false);
                        return;
                    }
                    const response = await vocabularyService.getVocabulary({ page: 1, pageSize: 1000 }, { search: value, tags: [] });
                    const items = response?.items ?? [];
                    setSearchResults(items);
                    setShowSearchDropdown(items.length > 0);
                }, 500);
            } else {
                setSearchResults([]);
                setShowSearchDropdown(false);
            }
        } else {
            if (value.length >= 3 || value.length === 0) {
                debounceRef.current = setTimeout(() => {
                    navigate(`/dashboard?search=${encodeURIComponent(value)}`);
                }, 500);
            }
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            setSearchBuffer('');
            setSearchResults([]);
            setShowSearchDropdown(false);
            if (!isWordPage() && searchBuffer !== '' && location.pathname === '/dashboard') {
                navigate('/dashboard?search=');
            }
            return;
        }
        if (e.key === 'Enter') {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (isWordPage()) {
                if (searchBuffer.length >= 3) {
                    vocabularyService.getVocabulary({ page: 1, pageSize: 1000 }, { search: searchBuffer, tags: [] })
                        .then(response => {
                            const items = response?.items ?? [];
                            setSearchResults(items);
                            setShowSearchDropdown(items.length > 0);
                        });
                }
            } else {
                setShowSearchDropdown(false);
                navigate(`/dashboard?search=${encodeURIComponent(searchBuffer)}`);
            }
        }
    };

    const handleSearchResultClick = (item: VocabularyItem) => {
        setShowSearchDropdown(false);
        setSearchBuffer('');
        navigate('/EditWord', { state: { selectedItem: item.id } });
    };

    const handleAddNew = () => {
        navigate('/addWord', { state: { word: searchBuffer } });
    };
    return (

        // <header className="header">
        //     <div className="logo" onClick={() => navigate('/')}>WeaveWords</div>
        //     <button className="hamburger" onClick={() => setIsMenuOpen(!isMenuOpen)}>
        //         <span></span>
        //         <span></span>
        //         <span></span>
        //     </button>
        //     <nav className={`navigation ${isMenuOpen ? 'open' : ''}`}>
        //         <li><Link to="/" onClick={() => setIsMenuOpen(false)}>Dashboard</Link></li>
        //         <li><Link to="/collections" onClick={() => setIsMenuOpen(false)}>Collections</Link></li>
        //         <li><Link to="/practice" onClick={() => setIsMenuOpen(false)}>Practice</Link></li>
        //         <li><Link to="/statistics" onClick={() => setIsMenuOpen(false)}>Statistics</Link></li>
        //         <li><Link to="/user" onClick={() => setIsMenuOpen(false)}>Tags</Link></li>
        //     </nav>
        //     <div className="user-avatar">
        //         <Link to="/profileSettings">
        //             {auth?.user.image_profile_id && showAvatar ? (
        //                 <img
        //                     src={`${BASE_URL}/users/avatar/${auth.user.id}?t=${avatarKey}`}
        //                     alt="Avatar"
        //                     className="nav-avatar"
        //                     onError={() => setShowAvatar(false)}
        //                 />
        //             ) : (
        //                 <div className="nav-avatar nav-avatar-initials">{initials}</div>
        //             )}
        //         </Link>
        //     </div>
        // </header>
        <>
            {isMenuOpen && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 198 }}
                    onClick={() => setIsMenuOpen(false)}
                />
            )}
            <div ref={topNavRef} className="top-nav">
                <div ref={headerRef} className="header">
                    <div className='logo' onClick={() => navigate('/dashboard')}>
                        <img src={logo} alt="WeaveWords" className='logo-mark' />
                        <div className='logo-text-group'>
                            <div className='logo-text'>
                                WeaveWords
                            </div>
                            <div className='logo-slogan'>
                                {t('slogan')}
                            </div>
                        </div>
                    </div>
                    <div>

                    </div>
                    <div className='menu-bar' data-tour="navigation-mobile" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                        <div className='menu-bar-line-one'></div>
                        <div className='menu-bar-line-two'></div>
                        <div className='menu-bar-line-one'></div>
                        <div className='menu-bar-line-two'></div>
                    </div>

                    <div className='avatar-menu'>
                        <button className="theme-toggle-btn" onClick={toggleTheme} title={t('toggle_theme', 'Toggle theme')}>
                            {theme === 'dark' ? '☀️' : '🌙'}
                        </button>
                        <div className='avatar'>
                            <Link to="/profileSettings">
                                {auth?.user.image_profile_id && showAvatar ? (
                                    <img
                                        src={`${BASE_URL}/users/avatar/${auth.user.id}?t=${avatarKey}`}
                                        alt={t('avatar', 'Avatar')}
                                        className="nav-avatar"
                                        onError={() => setShowAvatar(false)}
                                    />
                                ) : (
                                    <div className="nav-avatar nav-avatar-initials">{initials}</div>
                                )}
                            </Link>
                        </div>
                    </div>
                    <div ref={menuRef} className={`menu-${isMenuOpen ? 'open' : 'close'}`} style={{ height: isMenuOpen && menuHeight ? menuHeight : 0 }}>


                        <div className="menu-nav-item" data-tour="navigation">
                            <span className="menu-nav-text"><Link to="/" onClick={() => setIsMenuOpen(false)}>{t('home')}</Link></span>
                            {/* <div className="menu-nav-count">1512</div> */}
                        </div>
                        <div className="menu-nav-item">
                            <span className="menu-nav-text"><Link to="/collections" onClick={() => setIsMenuOpen(false)}>{t('collections')}</Link></span>
                            {/* <div className="menu-nav-count">14</div> */}
                        </div>
                        <div className="menu-nav-item">
                            <span className="menu-nav-text"><Link to="/practice" onClick={() => setIsMenuOpen(false)}>{t('practice')}</Link></span>
                        </div>
                        {/* Visibility is the lesson_mode feature flag alone. Pro only gates generating a
                            lesson (<ProGate> in CreateLesson + RequirePro on the API), so a free
                            user with the flag sees Lessons and gets the upgrade prompt there. */}
                        {lessonModeEnabled && (
                            <div className="menu-nav-item">
                                <span className="menu-nav-text"><Link to="/lessons" onClick={() => setIsMenuOpen(false)}>{t('lessons', 'Lessons')}</Link></span>
                            </div>
                        )}
                        <div className="menu-nav-item">
                            <span className="menu-nav-text">
                                <Link to="/spaced-repetition" onClick={() => setIsMenuOpen(false)}>{t('review')}</Link>
                                {showReviewBadge && (
                                    <span className="menu-nav-badge-dot" data-tooltip={t('review_due_badge', 'Review not started today')}></span>
                                )}
                            </span>
                        </div>
                        <div className="menu-nav-item">
                            <span className="menu-nav-text"><Link to="/statistics" onClick={() => setIsMenuOpen(false)}>{t('statistics')}</Link></span>
                            {/* <div className="menu-nav-count">7 Day Streak</div> */}
                        </div>
                        {tier === 2 && (
                            <div className="menu-nav-item">
                                <span className="menu-nav-text"><Link to="/user" onClick={() => setIsMenuOpen(false)}>{t('tags')}</Link></span>
                            </div>
                        )}
                        <div className="menu-nav-item menu-nav-item-settings">
                            <span className="menu-nav-text"><Link to="/profileSettings" onClick={() => setIsMenuOpen(false)}>{t('user_settings', 'User Settings')}</Link></span>
                        </div>

                        {/* <li><Link to="/" onClick={() => setIsMenuOpen(false)}>Dashboard</Link></li>
                <li><Link to="/collections" onClick={() => setIsMenuOpen(false)}>Collections</Link></li>
                <li><Link to="/practice" onClick={() => setIsMenuOpen(false)}>Practice</Link></li>
                <li><Link to="/statistics" onClick={() => setIsMenuOpen(false)}>Statistics</Link></li>
                <li><Link to="/user" onClick={() => setIsMenuOpen(false)}>Tags</Link></li> */}
                    </div>
                </div>
                <div className='search'>
                    <div className='search-bar' style={{ position: 'relative' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
                        <input
                            className='search-bar-input'
                            type="text"
                            placeholder={t('search_placeholder')}
                            value={searchBuffer}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            onBlur={() => setTimeout(() => setShowSearchDropdown(false), 150)}
                            onFocus={() => searchBuffer.length >= 3 && searchResults.length > 0 && setShowSearchDropdown(true)}
                        />
                        <MicButton onResult={handleVoiceResult} className="search-bar-mic" />
                        {showSearchDropdown && (
                            <div className="nav-search-dropdown">
                                {searchResults.slice(0, 5).map((item) => (
                                    <div
                                        key={item.id}
                                        className="nav-search-result-item"
                                        onMouseDown={() => handleSearchResultClick(item)}
                                    >
                                        <div className="nav-result-word">{item.word}</div>
                                        <div className="nav-result-translation">{item.translation.join(', ')}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className={`new-button${location.pathname.startsWith('/spaced-repetition') || location.pathname.startsWith('/practice') ? ' hidden' : ''}`}>
                    <span className='new-button-text' onClick={handleAddNew}>{t('new_word')}</span>
                </div>
                <div className={`stats-control${location.pathname.startsWith('/spaced-repetition') || location.pathname.startsWith('/practice') ? ' hidden' : ''}`}>
                    <div>
                        <div className="stats-label">{t('vocabulary_label')}</div>
                        <div className='stats-number-of-words'>
                            {totalItems !== null ? totalItems.toLocaleString() : '—'} <span className='stats-text'>{t('words')}</span></div>
                    </div>
                    <div className='nav-mobile-challenge' data-tour="daily-challenge" onClick={() => navigate('/spaced-repetition')}>
                        <span className='nav-mobile-challenge-label'>{(streakDays !== null) ? <>{t('challenge', 'Challenge')}  <StreakBadge streakDays={streakDays} todayXP={todayXP} compact /></> : t('start_challenge', 'Start Challenge')}</span>
                    </div>
                    {location.pathname === '/dashboard' && (
                        <div className='navigation-dashboard-control'>
                            <div className='dashboard-control-tile' onClick={handleToggleLayout} title={layoutMode === 'cards' ? t('table_view', 'Table view') : t('card_view', 'Card view')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect></svg>
                            </div>
                            <div className='dashboard-control-list' onClick={handleToggleView} title={viewMode === 'last10' ? t('view_paginated', 'Paginated') : viewMode === 'paginated' ? t('view_full_list', 'Full list') : t('view_last_10', 'Last 10')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>

    );
}

export default Navigation;